import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type IIconsVector, type IStatusProps } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SendFromStore } from 'widgets/SendFrom'

export class RcsStore {
  private _sendFromStore: SendFromStore
  private _disposeRcsReaction: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore) {
    this._sendFromStore = new SendFromStore(this._entities, {
      onChangeInboxId: (id) => {
        if (!id) return
        this.onUpdate({ id })
      },
    })
    makeAutoObservable(this)
    this.reactionRcs()
  }

  get sendFromStore() {
    return this._sendFromStore
  }

  get loading() {
    return this._entities.rcsStore.loading
  }

  get isEmpty() {
    return !this._entities.rcsStore.rcs
  }

  get rcs() {
    return this._entities.rcsStore.rcs
  }

  get statusTitleMap(): Record<string, string> {
    return {
      in_progress: 'In Progress',
      approved: 'Approved',
      rejected: 'Rejected',
      under_review: 'Pending Review',
      published: 'In Carrier Review',
    }
  }

  get statusIntentMap(): Record<string, IStatusProps['intent']> {
    return {
      in_progress: 'informative',
      approved: 'positive',
      rejected: 'negative',
      under_review: 'informative',
      published: 'purple',
    }
  }

  get statusIconMap(): Record<string, IIconsVector> {
    return {
      in_progress: 'clock',
      approved: 'check2',
      rejected: 'failed',
      under_review: 'clock',
      published: 'clock',
    }
  }

  init = async () => {
    if (this.rcs?.fallbackInboxId)
      this._sendFromStore.setSelectItem({ inbox_id: this.rcs.fallbackInboxId })

    if (this.rcs) return

    await this._entities.rcsStore.init()
  }

  onUpdate = async (fallbackInbox: { id: number }) =>
    await this._entities.rcsStore.onUpdate(fallbackInbox)

  reset = () => {
    this._sendFromStore.reset()
  }

  dispose = () => {
    this._disposeRcsReaction?.()
  }

  reactionRcs = () => {
    this._disposeRcsReaction?.()
    this._disposeRcsReaction = reaction(
      () => this._entities.rcsStore.rcs,
      (rcs) => {
        if (rcs?.fallbackInboxId)
          this._sendFromStore.setSelectItem({ inbox_id: rcs.fallbackInboxId })
      }
    )
  }
}
